import { lookupVin } from '@/apiCalls/assetLookup';
import { createOrUpdateBorrower } from '@/apiCalls/borrower';
import { Button, EButtonType } from '@/components/Button';
import {
    ECondition,
    ELoanUsage,
    EUserFlow,
    EVehicleAssetType,
    selectIsVehicleKnown,
    selectPurchasePrice,
    selectVehicleAssetType,
    selectVehicleCondition,
    setIsVehicleRegistrationKnown,
} from '@/context/borrower';
import {
    EDisplays,
    EModals,
    selectIsAssetKnownViaParams,
    selectPartner,
    selectPartnerOverwriteId,
    selectUserFlow,
    setDisplay,
    setIsAssetKnownViaParams,
    setModal,
} from '@/context/display';
import { store } from '@/context/store';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { selectIsLoggedIn } from '@/context/user';
import { EConversionType, reportConversion } from '@/utils/reportConversion';

interface QuickCalculatorButtonProps {
    setShowErrors: React.Dispatch<React.SetStateAction<boolean>>;
}

export function QuickCalculatorButton({
    setShowErrors,
}: QuickCalculatorButtonProps): JSX.Element {
    const partnerId = useAppSelector(
        (state) => state.displayReducer.partner.id
    );
    const glassValuation = useAppSelector(
        (state) => state.borrowerReducer.vehicle.glassValuation
    );
    const userFlow = useAppSelector(selectUserFlow);
    const isLoggedIn = useAppSelector(selectIsLoggedIn);
    const purchasePrice = useAppSelector(selectPurchasePrice);
    const partner = useAppSelector(selectPartner);
    const { storeSelectorOptions } = partner;
    const partnerOverwriteId = useAppSelector(selectPartnerOverwriteId);
    const dispatch = useAppDispatch();
    const isAssetTypePersonalLoan =
        useAppSelector(selectVehicleAssetType) ===
        EVehicleAssetType.PERSONAL_LOAN;
    async function handleClick(): Promise<void> {
        if (!purchasePrice) {
            setShowErrors(true);
            return;
        }
        setShowErrors(false);
        // User Flow for A
        if (
            userFlow === EUserFlow.NORMAL ||
            userFlow === EUserFlow.NORMAL.toLocaleUpperCase()
        ) {
            normalUserFlow();
        }
        if (
            userFlow === EUserFlow.EARLY_SIGNUP ||
            userFlow === EUserFlow.EARLY_SIGNUP.toLocaleUpperCase() ||
            userFlow === EUserFlow.INSTANT_LEAD_CAPTURE ||
            userFlow === EUserFlow.INSTANT_LEAD_CAPTURE.toLocaleUpperCase()
        ) {
            if (isLoggedIn || storeSelectorOptions) {
                normalUserFlow();
            } else {
                dispatch(setModal(EModals.REGISTER));
            }
        }
    }
    const isVehicleKnown = useAppSelector(selectIsVehicleKnown);
    const isVehicleNew =
        useAppSelector(selectVehicleCondition) === ECondition.NEW;
    const isAssetKnownViaParams = useAppSelector(selectIsAssetKnownViaParams);
    const disabledGlassesGuideVinLookup = useAppSelector(
        (state) => state.displayReducer.partner.disabledGlassesGuideVinLookup
    );
    async function normalUserFlow(): Promise<void> {
        // Only lookup the VIN if we have a partnerId and it's not UNKNOWN
        // and glasses guide VIN lookup is not disabled
        if (
            !glassValuation &&
            partnerId &&
            partnerId !== 'UNKNOWN' &&
            !disabledGlassesGuideVinLookup
        ) {
            await lookupVin(partnerId).then((isSuccessful) => {
                if (isSuccessful) {
                    dispatch(setIsAssetKnownViaParams(true));
                }
            });
        }
        reportConversion(EConversionType.LEAD_CAPTURE);

        createOrUpdateBorrower();
        if (isAssetTypePersonalLoan) {
            dispatch(setDisplay(EDisplays.BASIC));
            return;
        }
        if (storeSelectorOptions && !partnerOverwriteId) {
            dispatch(setDisplay(EDisplays.STORE_SELECTOR));
        } else {
            const state = store.getState();

            const isNovatedLease =
                state.borrowerReducer.loan.loanUsage === ELoanUsage.NOVATED;

            if (isNovatedLease && isAssetKnownViaParams) {
                dispatch(setDisplay(EDisplays.BASIC));
                return;
            }

            if (isNovatedLease && !isAssetKnownViaParams) {
                if (isVehicleNew) {
                    dispatch(setIsVehicleRegistrationKnown(false));
                } else {
                    dispatch(setIsVehicleRegistrationKnown(true));
                }
                dispatch(setDisplay(EDisplays.VEHICLE));
                return;
            }
            dispatch(setDisplay(EDisplays.LOAN));
        }
    }

    return (
        <Button
            onClick={handleClick}
            title='Get Personalised Quote'
            type={EButtonType.PRIMARY}
            id='screen-quickCalculator-button-getQuoteNow'
        />
    );
}
